import React, { forwardRef, useMemo, type ComponentPropsWithoutRef } from 'react';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { useGetLinkProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-link-props/index.tsx';
import { subspaceEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-subspaces/src/subspaceEntryPoint.entrypoint.tsx';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { useExpandableEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import { RouteEntryPointContainer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/route-entry-point-adapter/index.tsx';
import { LEFT_SIDEBAR } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import type { LeftSidebarEntryPointConfig } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
	type ExpandableMenuItemTriggerProps,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../common/constants.tsx';

type Nav4ExpandableMenuItemProps = ComponentPropsWithoutRef<typeof Nav4ExpandableMenuItem>;

export type JiraBusinessProjectExpandableMenuOwnProps = Nav4ExpandableMenuItemProps & {
	projectKey: string;
	projectUrl: string;
	visualContentRef: Nav4ExpandableMenuItemProps['dropTargetRef'];
};

type JiraBusinessProjectExpandableMenuRef = HTMLAnchorElement | HTMLButtonElement;

export type JiraBusinessProjectExpandableMenuProps<
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	RouterLinkConfig extends Record<string, any> = never,
> = JiraBusinessProjectExpandableMenuOwnProps & ExpandableMenuItemTriggerProps<RouterLinkConfig>;

function JiraBusinessProjectExpandableMenuContent<
	RouterLinkConfig extends Record<string, any> = never, // oxlint-disable-line @typescript-eslint/no-explicit-any
>(
	{
		visualContentRef,
		dropIndicator,
		isDragging,
		hasDragIndicator,
		elemBefore,
		menuId,
		actionsOnHover,
		children,
		projectKey,
		projectUrl,
		...triggerProps
	}: JiraBusinessProjectExpandableMenuProps<RouterLinkConfig>,
	expandableRef: React.Ref<JiraBusinessProjectExpandableMenuRef>,
): React.ReactElement | null {
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const {
		data: {
			user: { accountId },
		},
	} = useCurrentUser();

	// Best available entrypoint pattern https://hello.atlassian.net/wiki/spaces/UAF/pages/2718064036/EntryPoints+FAQs#TS2345-%3A-Argument-type-%E2%80%A6getPreloadProps%3A-()-%3D%3E-%7B%7D%3B-%7D-is-not-assignable-to-parameter-of-type-'never'.
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const entryPointParams = useMemo(
		// oxlint-disable-line typescript/consistent-type-assertions
		() => ({
			tenantContext: { cloudId, activationId, atlassianAccountId: accountId || '' },
			context: {
				match: {
					params: {
						projectKey,
					},
				},
			},
		}),
		[accountId, activationId, cloudId, projectKey],
	) as unknown as EntryPointRouteParams;

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		subspaceEntryPoint,
		entryPointParams,
	);

	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const leftSidebarEPRef = usePageLayoutEntryPoint(LEFT_SIDEBAR) as LeftSidebarEntryPointConfig; // oxlint-disable-line typescript/consistent-type-assertions
	const subspacesEPRef = leftSidebarEPRef?.entryPoints?.subspaces;

	const expandableMenuItemTriggerRef = useMergeRefs<JiraBusinessProjectExpandableMenuRef>(
		expandableRef,
		triggerRef,
	);

	const epContainerProps = useMemo(
		() => ({
			id: 'business-expandable-project',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
			errorFallback: 'flag' as const, // oxlint-disable-line typescript/consistent-type-assertions
			fallback: <SkeletonNavMenuItems />,
			// oxlint-disable-next-line typescript/consistent-type-assertions
			runtimeProps: {} as never, // oxlint-disable-line typescript/consistent-type-assertions
		}),
		[],
	);

	// Compute isSelected based on current path
	const { getLinkProps } = useGetLinkProps();
	const { isSelected } = getLinkProps(projectUrl);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			menuId={menuId}
			onExpansionToggle={() => {
				entryPointActions.load();
			}}
		>
			<ExpandableMenuItemTrigger<RouterLinkConfig>
				isDragging={isDragging}
				hasDragIndicator={hasDragIndicator}
				elemBefore={elemBefore}
				actionsOnHover={actionsOnHover}
				ref={expandableMenuItemTriggerRef}
				interactionName="nav4-sidebar-business-expandable-project"
				isSelected={isSelected}
				{...triggerProps}
			>
				{children}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<RouteEntryPointContainer
					epContainerProps={epContainerProps}
					jiraEPRef={entryPointReferenceSubject}
					nestedEPRef={subspacesEPRef}
					placeholderEntryPoint={subspaceEntryPoint}
				/>
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

// oxlint-disable-next-line typescript/consistent-type-assertions
// oxlint-disable-next-line typescript/consistent-type-assertions
export const JiraBusinessProjectExpandableMenu = forwardRef(
	JiraBusinessProjectExpandableMenuContent,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
) as <RouterLinkConfig extends Record<string, any> = never>(
	props: JiraBusinessProjectExpandableMenuProps<RouterLinkConfig> & {
		ref?: React.Ref<JiraBusinessProjectExpandableMenuRef>;
	},
) => React.ReactElement | null;
